import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Home as HomeIcon, User, Building2, Wrench } from 'lucide-react';
import GoogleAuthButton from '../components/GoogleAuthButton';

export default function Register() {
  const location = useLocation();
  // Check if coming from contractor registration link
  const isContractorRoute = location.pathname === '/contractor/register';
  const [role, setRole] = useState(isContractorRoute ? 'contractor' : '');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!role) {
      toast.error('Please select your role');
      return;
    }

    setLoading(true);

    try {
      const user = await register({ ...formData, role });
      toast.success('Account created successfully!');
      
      if (user.role === 'tenant') {
        navigate('/tenant/dashboard');
      } else if (user.role === 'landlord') {
        navigate('/landlord/dashboard');
      } else if (user.role === 'contractor') {
        navigate('/contractor/dashboard');
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle validation errors from FastAPI
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => {
            const field = err.loc?.[err.loc.length - 1] || 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
          toast.error(errorMessages);
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          toast.error(detail);
        }
        // If detail is an object
        else {
          toast.error(detail.msg || JSON.stringify(detail));
        }
      } else {
        toast.error(error.message || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!role) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <div className="w-full max-w-3xl">
          <div className="text-center mb-12">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary rounded-lg">
                <HomeIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-display font-extrabold text-primary">LetWise</h1>
            </Link>
            <h2 className="text-4xl font-display font-bold text-slate-900 mb-2">Join LetWise</h2>
            <p className="text-xl text-slate-600">Choose your account type to get started</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <button
              onClick={() => setRole('tenant')}
              className="bg-white rounded-2xl p-8 border-2 border-slate-200 hover:border-accent transition-all hover:shadow-xl group text-left"
            >
              <div className="p-4 bg-accent/10 rounded-xl w-fit mb-6 group-hover:scale-110 transition-transform">
                <User className="w-10 h-10 text-accent" />
              </div>
              <h3 className="text-xl font-display font-bold mb-2">I'm a Tenant</h3>
              <p className="text-slate-600">Find and apply for properties, manage tenancies, and pay rent online</p>
            </button>

            <button
              onClick={() => setRole('landlord')}
              className="bg-white rounded-2xl p-8 border-2 border-slate-200 hover:border-primary transition-all hover:shadow-xl group text-left"
            >
              <div className="p-4 bg-primary/10 rounded-xl w-fit mb-6 group-hover:scale-110 transition-transform">
                <Building2 className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-xl font-display font-bold mb-2">I'm a Landlord</h3>
              <p className="text-slate-600">List properties, screen tenants, and manage your portfolio efficiently</p>
            </button>

            <button
              onClick={() => setRole('contractor')}
              className="bg-white rounded-2xl p-8 border-2 border-slate-200 hover:border-orange-500 transition-all hover:shadow-xl group text-left"
            >
              <div className="p-4 bg-orange-100 rounded-xl w-fit mb-6 group-hover:scale-110 transition-transform">
                <Wrench className="w-10 h-10 text-orange-500" />
              </div>
              <h3 className="text-xl font-display font-bold mb-2">I'm a Contractor</h3>
              <p className="text-slate-600">Join our network, receive job alerts, and grow your trade business</p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary rounded-lg">
              <HomeIcon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-display font-extrabold text-primary">LetWise</h1>
          </Link>
          <h2 className="text-3xl font-display font-bold text-slate-900 mb-2">Create Account</h2>
          <p className="text-slate-600">Registering as a {role}</p>
          <button onClick={() => setRole('')} className="text-primary text-sm mt-2 hover:underline">
            Change role
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">First Name</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Last Name</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition"
                required
                minLength={8}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-4 text-lg disabled:opacity-50 mt-6"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <GoogleAuthButton text="Sign up with Google" />

          <div className="mt-6 text-center">
            <p className="text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-semibold hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}